import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useT } from "@/lib/i18n";
import { api, errMsg, fmtDate, todayStr, periodRange, rangeDays, wd } from "@/lib/api";
import { Card, Loader, PageTitle, PositionBadge, TimeInput } from "@/components/kv";
import { ModeToggle, PeriodNav } from "../staff/StaffSchedule";

const LEAVE_TYPES = ["annual", "daily", "sick", "other"];

const CellDialog = ({ cell, positions, onClose, onChanged }) => {
  const { t, lang } = useT();
  const pos = positions.find((p) => p.key === cell.user.position);
  const [start, setStart] = useState(cell.entry.start || pos?.default_start || "12:00");
  const [end, setEnd] = useState(cell.entry.end || pos?.default_end || "20:00");
  const [leaveType, setLeaveType] = useState("annual");
  const base = { user_id: cell.user.id, date: cell.ds };

  const run = async (fn) => {
    try {
      await fn();
      toast.success(t("common.saved"));
      onChanged();
      onClose();
    } catch (e) {
      toast.error(errMsg(e, t));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#FAF6F0] rounded-2xl" data-testid="shift-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{t("planner.edit")}</DialogTitle>
          <DialogDescription>{cell.user.name} · <span className="capitalize">{fmtDate(cell.ds, lang, { weekday: "long", day: "numeric", month: "long" })}</span></DialogDescription>
        </DialogHeader>
        {cell.entry.type === "leave" ? (
          <div className="space-y-3">
            <p className="rounded-xl bg-violet-50 text-violet-800 p-3 text-sm font-semibold">{t(`leaves.types.${cell.entry.leave_type}`)}</p>
            <Button variant="destructive" className="w-full rounded-full" data-testid="shift-remove-leave-button"
              onClick={() => run(() => api.delete(`/admin/leaves/${cell.entry.leave_id}`))}>{t("planner.remove_leave")}</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>{t("common.start")}</Label><TimeInput value={start} onChange={setStart} allowClear={false} className="bg-white font-mono-kv w-full" data-testid="shift-start-input" /></div>
              <div className="space-y-1"><Label>{t("common.end")}</Label><TimeInput value={end} onChange={setEnd} allowClear={false} className="bg-white font-mono-kv w-full" data-testid="shift-end-input" /></div>
            </div>
            <Button className="w-full rounded-full" data-testid="shift-save-button" onClick={() => run(() => api.put("/admin/shifts", { ...base, start, end }))}>{t("planner.save_custom")}</Button>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" className="rounded-full" data-testid="shift-off-button" onClick={() => run(() => api.put("/admin/shifts", { ...base, off: true }))}>{t("planner.set_off")}</Button>
              <Button variant="outline" className="rounded-full" data-testid="shift-reset-button" onClick={() => run(() => api.delete("/admin/shifts", { params: base }))}>{t("planner.reset")}</Button>
            </div>
            <div className="border-t border-[#E8DFD0] pt-4 flex gap-2">
              <select value={leaveType} onChange={(e) => setLeaveType(e.target.value)} className="kv-input flex-1" data-testid="shift-leave-type-select">
                {LEAVE_TYPES.map((x) => <option key={x} value={x}>{t(`leaves.types.${x}`)}</option>)}
              </select>
              <Button variant="secondary" className="rounded-full" data-testid="shift-add-leave-button"
                onClick={() => run(() => api.post("/admin/leaves", { user_id: cell.user.id, start_date: cell.ds, end_date: cell.ds, type: leaveType, note: "" }))}>{t("planner.add_leave")}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PositionDefaults = ({ positions, onSaved }) => {
  const { t } = useT();
  const [vals, setVals] = useState({});
  useEffect(() => {
    setVals(Object.fromEntries(positions.map((p) => [p.key, { default_start: p.default_start, default_end: p.default_end }])));
  }, [positions]);
  const save = async (key) => {
    try {
      await api.put(`/admin/positions/${key}`, vals[key]);
      toast.success(t("common.saved"));
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, t));
    }
  };
  const set = (key, f, v) => setVals((s) => ({ ...s, [key]: { ...s[key], [f]: v } }));
  return (
    <Card className="p-5 mt-6" data-testid="position-defaults-card">
      <h2 className="font-display text-2xl font-bold">{t("planner.defaults")}</h2>
      <p className="text-sm text-stone-500 mb-4">{t("planner.defaults_hint")}</p>
      <div className="grid md:grid-cols-2 gap-3">
        {positions.map((p) => vals[p.key] && (
          <div key={p.key} className="flex flex-wrap items-center gap-2 rounded-xl bg-[#FAF6F0] p-3">
            <span className="w-full sm:w-36 text-sm font-semibold">{t(`positions.${p.key}`)}</span>
            <TimeInput value={vals[p.key].default_start} allowClear={false} onChange={(v) => set(p.key, "default_start", v)} data-testid={`pos-start-${p.key}`} />
            <span>–</span>
            <TimeInput value={vals[p.key].default_end} allowClear={false} onChange={(v) => set(p.key, "default_end", v)} data-testid={`pos-end-${p.key}`} />
            <Button size="sm" className="rounded-full" onClick={() => save(p.key)} data-testid={`pos-save-${p.key}`}>{t("common.save")}</Button>
          </div>
        ))}
      </div>
    </Card>
  );
};

const cellTone = (e) =>
  e.type === "shift" ? (e.custom ? "bg-amber-50 border-amber-300" : "bg-white") : e.type === "leave" ? "bg-violet-50 text-violet-800 border-violet-200" : "bg-[#F3EDE2] text-stone-400";

export default function Planner() {
  const { t, lang } = useT();
  const [mode, setMode] = useState("week");
  const [anchor, setAnchor] = useState(todayStr());
  const [data, setData] = useState(null);
  const [positions, setPositions] = useState([]);
  const [cell, setCell] = useState(null);
  const [s, e] = periodRange(mode, anchor);
  const today = todayStr();

  const load = useCallback(() => {
    api.get("/admin/schedule", { params: { start: s, end: e } }).then((r) => setData(r.data)).catch((err) => toast.error(errMsg(err, t)));
    api.get("/positions").then((r) => setPositions(r.data));
  }, [s, e, t]);

  useEffect(() => {
    load();
  }, [load]);

  const days = rangeDays(s, e);
  return (
    <div>
      <PageTitle kicker={t("adminnav.planner")} title={t("planner.title")}>
        <ModeToggle mode={mode} setMode={setMode} prefix="planner" />
        <PeriodNav mode={mode} anchor={anchor} setAnchor={setAnchor} prefix="planner" />
      </PageTitle>
      <p className="text-sm text-stone-500 mb-4">{t("planner.hint")}</p>
      {!data ? <Loader /> : (
        <Card className="overflow-x-auto" data-testid="planner-grid">
          <table className="border-separate border-spacing-1 text-xs">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-white min-w-[140px] text-left p-2">{t("common.staff")}</th>
                {days.map((ds) => (
                  <th key={ds} className={`p-1 font-medium ${ds === today ? "text-[#b8563a]" : "text-stone-500"} ${mode === "week" ? "min-w-[96px]" : "min-w-[58px]"}`}>
                    <div>{t("days")[wd(ds)]}</div>
                    <div className="font-mono-kv text-sm">{mode === "week" ? fmtDate(ds, lang, { day: "numeric", month: "short" }) : Number(ds.slice(8))}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.id}>
                  <td className="sticky left-0 z-10 bg-white p-2 align-middle">
                    <div className="font-semibold text-sm whitespace-nowrap">{u.name}</div>
                    <PositionBadge position={u.position} />
                  </td>
                  {days.map((ds) => {
                    const en = data.schedule[u.id]?.[ds];
                    if (!en) return <td key={ds} />;
                    return (
                      <td key={ds}>
                        <button onClick={() => setCell({ user: u, ds, entry: en })} data-testid={`planner-cell-${u.username}-${ds}`}
                          className={`w-full h-14 rounded-lg border border-[#E8DFD0] px-1 font-mono-kv leading-tight hover:ring-2 hover:ring-[#c86d51] transition-shadow ${cellTone(en)} ${ds === today ? "ring-1 ring-[#c86d51]" : ""}`}>
                          {en.type === "shift" ? <>{en.start}<br />{en.end}</> : en.type === "leave" ? t(`leaves.types.${en.leave_type}`) : t("status.off")}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <PositionDefaults positions={positions} onSaved={load} />
      {cell && <CellDialog cell={cell} positions={positions} onClose={() => setCell(null)} onChanged={load} />}
    </div>
  );
}
